/**
 * App Stage Configuration
 * 
 * Provides utilities for determining the current deployment stage (dev/prod/local)
 * and retrieving environment-specific configuration values.
 * 
 * Usage:
 * ```
 * const loginUrl = getAuthUrl('login'); // Returns correct URL based on env
 * const apiUrl = getUrlByStage({ dev: 'dev-api', prod: 'prod-api' });
 * ```
 */

export type AppStage = 'dev' | 'prod' | 'local';

/**
 * Determines the current app deployment stage
 * Uses NEXT_PUBLIC_API_STAGE env var (set from $app.stage via SST / ENVIRONMENT in GitHub Actions)
 * 
 * NOTE: ENVIRONMENT variable is mapped to NEXT_PUBLIC_API_STAGE during deployment
 */
export function getAppStage(): AppStage {
  // Check NEXT_PUBLIC_API_STAGE (set from $app.stage in sst.config.ts)
  const stage = process.env.NEXT_PUBLIC_API_STAGE;
  
  if (stage === 'prod' || stage === 'production') {
    return 'prod';
  }
  
  if (stage === 'dev' || stage === 'development') {
    return 'dev';
  }
  
  if (stage === 'local') {
    return 'local';
  }
  
  // Fallback to NODE_ENV if not set
  if (process.env.NODE_ENV === 'production') {
    return 'prod';
  }
  
  if (process.env.NODE_ENV === 'development') {
    return 'dev';
  }
  
  return 'local';
}

/**
 * Returns a value based on the current app stage
 * Useful for getting environment-specific configuration
 * 
 * @example
 * const apiUrl = getUrlByStage({
 *   dev: 'https://dev-api.example.com',
 *   prod: 'https://api.example.com',
 *   local: 'http://localhost:4000'
 * });
 */
export function getValueByStage<T>(options: {
  prod: T;
  dev: T;
  local?: T;
}, stage?: AppStage): T {
  const appStage = stage || getAppStage();
  
  switch (appStage) {
    case 'prod':
      return options.prod;
    case 'dev':
      return options.dev;
    case 'local':
    default:
      return options.local ?? options.dev;
  }
}

/**
 * Returns the base URL for the app based on deployment stage
 * NOTE: Local development always routes to dev environment (no localhost auth)
 */
export function getBaseUrl(stage?: AppStage): string {
  return getValueByStage({
    prod: 'https://app.trackeros.co',
    dev: 'https://dev.app.londongrade.com',
    local: 'https://dev.app.londongrade.com', // Local routes to dev, not localhost
  }, stage);
}

/**
 * Returns the full auth URL (login or register) for the current stage
 * 
 * @example
 * const loginUrl = getAuthUrl('login');  // https://app.trackeros.co/auth/login (in prod)
 * const signupUrl = getAuthUrl('register');  // https://dev.app.londongrade.com/auth/register (in dev)
 */
export function getAuthUrl(path: 'login' | 'register', stage?: AppStage): string {
  const baseUrl = getBaseUrl(stage);
  return `${baseUrl}/auth/${path}`;
}

/**
 * Processes a URL and converts relative auth URLs to absolute environment-specific URLs
 * Use this helper when rendering links from data files that contain /auth/* paths
 * 
 * @example
 * const href = processAuthUrl('/auth/register');  // Returns https://app.trackeros.co/auth/register (in prod)
 * const href = processAuthUrl('/pricing');  // Returns /pricing (unchanged)
 */
export function processAuthUrl(url: string, stage?: AppStage): string {
  if (url.startsWith('/auth/login')) {
    return getAuthUrl('login', stage);
  }
  if (url.startsWith('/auth/register')) {
    return getAuthUrl('register', stage);
  }
  return url;
}
